"use client";

import * as React from "react";

/**
 * A layout-settle window: a short span in which an ancestor is animating a
 * LAYOUT property — the shell pane's width — so every animation frame already
 * relayouts the whole page. Measurement consumers (`useElementSize`, the
 * widget card's observers, `ContentSizeProvider`) read
 * {@link useLayoutSettling} and hold their ResizeObserver-driven state commits
 * while it is true: the animation then pays only CSS layout per frame, and the
 * per-frame React re-render + forced-layout storm collapses into ONE commit
 * when the window closes. Without the hold, a 300ms pane slide re-renders
 * every widget and forces synchronous layout per card per frame, which drops
 * most of the transition's frames.
 *
 * The provider rides inside `AppShellProvider`, so an app composed on the
 * shell gets the wiring for free; outside any provider both hooks are inert
 * (`false`, no-op) and consumers behave exactly as before.
 */

const LayoutSettlingContext = React.createContext(false);

const BeginLayoutSettleContext = React.createContext<
  (() => () => void) | null
>(null);

export function LayoutSettleProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // A counter, not a boolean: two windows can overlap (the sidebar pane and a
  // right-panel island animating together), and the first to land must not
  // release the other's hold.
  const [count, setCount] = React.useState(0);
  const begin = React.useCallback(() => {
    setCount((current) => current + 1);
    // Idempotent release: a window is closed by whichever of `transitionend`
    // and the caller's backstop timer arrives first, and the loser must not
    // decrement a second time.
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCount((current) => Math.max(0, current - 1));
    };
  }, []);
  return (
    <BeginLayoutSettleContext.Provider value={begin}>
      <LayoutSettlingContext.Provider value={count > 0}>
        {children}
      </LayoutSettlingContext.Provider>
    </BeginLayoutSettleContext.Provider>
  );
}

/**
 * True while a layout-settle window is open. Measurement consumers defer
 * their resize-driven state commits while this reads true and flush once when
 * it flips false. Always false outside a {@link LayoutSettleProvider}.
 */
export function useLayoutSettling(): boolean {
  return React.useContext(LayoutSettlingContext);
}

/**
 * Wraps a measurement so it runs at once outside a settle window and is
 * parked while one is open, then runs ONCE when the window closes. The
 * returned function is stable; hand it to a `ResizeObserver` or a resize
 * listener. The first, mounting measurement is the caller's own call and is
 * never held: a consumer mounting mid-window still needs a first size.
 */
export function useSettledMeasure(measure: () => void): () => void {
  const settling = useLayoutSettling();
  const settlingRef = React.useRef(settling);
  settlingRef.current = settling;
  const measureRef = React.useRef(measure);
  measureRef.current = measure;
  const pendingRef = React.useRef(false);

  const run = React.useCallback(() => {
    if (settlingRef.current) {
      pendingRef.current = true;
      return;
    }
    pendingRef.current = false;
    measureRef.current();
  }, []);

  // Before paint, so the settled size lands with the final frame of the
  // animation instead of one after it.
  React.useLayoutEffect(() => {
    if (settling || !pendingRef.current) return;
    pendingRef.current = false;
    measureRef.current();
  }, [settling]);

  return run;
}

const noopRelease = (): void => {};
const noopBegin = (): (() => void) => noopRelease;

/**
 * Opens a settle window: call the returned function when the layout animation
 * starts and call ITS return value to close the window (idempotent). A no-op
 * outside a {@link LayoutSettleProvider}.
 */
export function useBeginLayoutSettle(): () => () => void {
  return React.useContext(BeginLayoutSettleContext) ?? noopBegin;
}
